import { z } from 'zod';

/**
 * Investment-size options, mirroring the legacy waitlist form's custom select
 * (`src/legacy/index.html`). The stored `value` is what the API receives; the
 * `label` is what the user sees.
 */
export const investmentSizeOptions = [
  { value: 'under_10k', label: 'Under $10,000' },
  { value: '10k_50k', label: '$10,000 – $50,000' },
  { value: '50k_250k', label: '$50,000 – $250,000' },
  { value: '250k_1m', label: '$250,000 – $1M' },
  { value: 'over_1m', label: '$1M+' },
] as const;

const investmentSizeValues = investmentSizeOptions.map((o) => o.value) as [
  string,
  ...string[],
];

export const waitlistSchema = z.object({
  name: z.string().trim().min(2, 'Please enter your name.'),
  email: z.string().trim().email('Please enter a valid email address.'),
  investmentSize: z.enum(investmentSizeValues, {
    errorMap: () => ({ message: 'Please select an investment size.' }),
  }),
});

export type WaitlistInput = z.infer<typeof waitlistSchema>;

/**
 * Safe-parse `input` against `waitlistSchema`, returning either the validated
 * data or a flat map of field name -> first error message.
 */
export function parseWaitlist(
  input: unknown
):
  | { ok: true; data: WaitlistInput }
  | { ok: false; errors: Record<string, string> } {
  const result = waitlistSchema.safeParse(input);
  if (result.success) {
    return { ok: true, data: result.data };
  }

  const fieldErrors = result.error.flatten().fieldErrors;
  const errors = Object.entries(fieldErrors).reduce<Record<string, string>>(
    (acc, [field, messages]) => {
      const message = messages?.[0];
      return message ? { ...acc, [field]: message } : acc;
    },
    {}
  );

  return { ok: false, errors };
}
